import {
  BBox,
  DEFAULT_HATCH_PATTERN,
  DEFAULT_TEXT_HEIGHT,
  Vec2,
  constraintEntityIds,
  deg2rad,
  describeConstraint,
  dimensionAxes,
  dimensionGeometry,
  dimensionValue,
  entityHandles,
  withHandlePoint,
  type Constraint,
  type ConstraintKind,
  type Entity,
  type EntityId,
} from '@lakaran/geometry';
import {
  CadDocument,
  Emitter,
  History,
  SnapEngine,
  Transaction,
  attachNearbyHandles,
  describeConstraintSet,
  entitiesCrossing,
  entitiesInWindow,
  hitTest,
  parseEntities,
  serializeEntities,
  solveDocument,
  stringifyTagged,
  type SnapCandidate,
  type SolveSummary,
} from '@lakaran/engine';
import { Viewport } from './viewport';
import { constrainToAngleStep } from './constraints';
import type { Tool, ToolField, ToolHost } from './tools/tool';
import { createDrawTools } from './tools/draw';
import { createModifyTools } from './tools/modify';
import { createAnnotationTools } from './tools/annotate';
import { createEditTools } from './tools/edit';
import { createConstraintTools } from './tools/constrain';
import { formatCapabilities, probeCapabilities } from '../caps';

export interface GridSettings {
  /** Minor grid spacing in millimetres. */
  size: number;
  visible: boolean;
  /** A heavier line every N minor lines. */
  majorEvery: number;
}

/**
 * Which constraint kinds the solver enforces. Turning a kind off leaves the constraints in the
 * document (they are shown, saved and counted) but stops them pulling the geometry.
 */
export const CONSTRAINT_KINDS: readonly ConstraintKind[] = [
  'coincident',
  'pointOnEdge',
  'distance',
  'fix',
  'horizontal',
  'vertical',
  'parallel',
  'perpendicular',
  'anchor',
];

export const CONSTRAINT_SHORT: Record<ConstraintKind, string> = {
  coincident: 'COIN',
  pointOnEdge: 'ON',
  distance: 'DIST',
  fix: 'FIX',
  horizontal: 'HOR',
  vertical: 'VER',
  parallel: 'PAR',
  perpendicular: 'PERP',
  anchor: 'ANCH',
};

export const CONSTRAINT_LABELS: Record<ConstraintKind, string> = {
  coincident: 'Coincident',
  pointOnEdge: 'Point on edge',
  distance: 'Distance',
  fix: 'Fix point',
  horizontal: 'Horizontal',
  vertical: 'Vertical',
  parallel: 'Parallel',
  perpendicular: 'Perpendicular',
  anchor: 'Anchor',
};

export interface EditorSettings {
  grid: GridSettings;
  textHeight: number;
  hatchPattern: string;
  /** Snap aperture in screen pixels (converted to millimetres per zoom). */
  snapRadiusPx: number;
  /** Pick aperture in screen pixels. */
  pickRadiusPx: number;
  /** Decimal places for millimetre values in the UI and dynamic input. */
  lengthDecimals: number;
  /** Decimal places for angular values. */
  angleDecimals: number;
  /** Dimension text height in millimetres (ISO 3098 default 3.5). */
  dimensionTextHeight: number;
  /** Dimension arrow length in millimetres. */
  dimensionArrowSize: number;
  /** Angle increment for the Ctrl constraint, in degrees (45 gives 45°/90°). */
  polarStepDeg: number;
  /** Constraint kinds the solver enforces; toggled from the toolbar chips. */
  enabledConstraints: Set<ConstraintKind>;
}

export interface PromptState {
  text: string;
  options: string[];
}

export interface FieldState {
  fields: ToolField[];
  activeIndex: number;
}

export interface EditorEvents extends Record<string, unknown> {
  /** Entities changed: rebuild static geometry. */
  document: void;
  /** Camera changed: grid and overlays need a refresh. */
  view: void;
  /** Draft, selection, snap marker or cursor changed. */
  overlay: void;
  cursor: { world: Vec2; screen: Vec2; snap: SnapCandidate | null };
  prompt: PromptState;
  message: string;
  selection: number;
  /** Dynamic numeric input for the active tool step. */
  fields: FieldState;
  /** A dimension was double-clicked: the UI opens its inline measurement editor. */
  dimensionEdit: { id: EntityId };
}

export type MouseButton = 'left' | 'middle' | 'right';

export interface Modifiers {
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
}

const TOOL_PROMPT = 'Command:';

/** Above this many constraints the solve is handed to a worker. */
const SOLVE_OFFLOAD_THRESHOLD = 120;

/**
 * Renderer-agnostic drafting editor. Owns the document, camera, snapping, selection,
 * history and the command interpreter; the UI layer only forwards input and draws.
 */
export class CadEditor implements ToolHost {
  readonly doc: CadDocument;
  readonly viewport = new Viewport();
  readonly history: History;
  readonly snapEngine = new SnapEngine();
  readonly events = new Emitter<EditorEvents>();
  readonly selection = new Set<EntityId>();

  settings: EditorSettings = {
    grid: { size: 10, visible: true, majorEvery: 10 },
    textHeight: DEFAULT_TEXT_HEIGHT,
    hatchPattern: DEFAULT_HATCH_PATTERN,
    snapRadiusPx: 12,
    pickRadiusPx: 8,
    lengthDecimals: 3,
    angleDecimals: 2,
    dimensionTextHeight: 3.5,
    dimensionArrowSize: 2.5,
    polarStepDeg: 45,
    enabledConstraints: new Set(CONSTRAINT_KINDS),
  };

  activeTool: Tool | null = null;
  draft: Entity[] = [];
  /** Procedural node output: drawn as a preview, never part of the document. */
  preview: Entity[] = [];
  cursorWorld: Vec2 = Vec2.of(0, 0);
  cursorScreen: Vec2 = Vec2.of(0, 0);
  activeSnap: SnapCandidate | null = null;
  promptState: PromptState = { text: TOOL_PROMPT, options: [] };
  lastCommand = '';
  selectionStart?: Vec2;
  selectionEnd?: Vec2;

  /** Dynamic input fields for the current tool step. */
  fields: ToolField[] = [];
  activeFieldIndex = 0;

  /** True while Ctrl is held: the cursor is constrained to `polarStepDeg` rays. */
  angleConstraint = false;

  /**
   * Optional off-thread solver, installed by the app shell. When absent (tests, headless
   * use) every solve runs inline.
   */
  offloadSolve?: (payload: { entities: string; constraints: string }) => Promise<
    { entities: string; changed: number } & SolveSummary
  >;

  private solving = false;
  private solveInFlight = false;
  private solveQueued = false;

  private reference?: Vec2;
  private snapCandidates: SnapCandidate[] = [];
  private snapCycle = -1;
  private fieldOverrides: Record<string, number> = {};
  private fieldToken = '';
  private readonly toolEntries: Array<{ names: string[]; make: () => Tool }>;

  constructor() {
    this.doc = new CadDocument();
    this.history = new History(this.doc);
    this.doc.events.on('change', () => this.events.emit('document', undefined));
    this.doc.events.on('layers', () => this.events.emit('document', undefined));
    this.history.onChange(() => this.events.emit('document', undefined));

    this.toolEntries = [
      ...createDrawTools(),
      ...createModifyTools(),
      ...createAnnotationTools(),
      ...createEditTools(),
      ...createConstraintTools(),
    ].map((make) => {
      const tool = make();
      return { names: [...tool.aliases, tool.name.toLowerCase()], make };
    });
  }

  // ---------------------------------------------------------------- ToolHost

  get editor(): CadEditor {
    return this;
  }

  prompt(text: string, options: string[] = []): void {
    this.promptState = { text, options };
    this.events.emit('prompt', this.promptState);
  }

  message(text: string): void {
    this.events.emit('message', text);
  }

  setDraft(entities: Entity[]): void {
    this.draft = entities;
    this.events.emit('overlay', undefined);
  }

  setNodePreview(entities: Entity[]): void {
    this.preview = entities;
    this.events.emit('overlay', undefined);
  }

  /** Clears the document and the undo history. */
  newDrawing(): void {
    if (this.activeTool) this.activeTool.cancel();
    this.doc.clear();
    this.history.clear();
    this.selection.clear();
    this.setNodePreview([]);
    this.events.emit('selection', 0);
    this.events.emit('overlay', undefined);
    this.message('NEW  Drawing cleared.');
  }

  setReference(point?: Vec2): void {
    this.reference = point;
  }

  endTool(): void {
    this.activeTool = null;
    this.draft = [];
    this.reference = undefined;
    this.clearFields();
    this.prompt(TOOL_PROMPT);
    this.events.emit('overlay', undefined);
  }

  commit(tx: Transaction, label: string): void {
    const added: Entity[] = [];
    for (const op of tx.ops) {
      if (op.kind === 'add') added.push(op.entity);
    }
    if (added.length > 0) attachNearbyHandles(this.doc, added, tx);
    const merging = !tx.isEmpty;
    this.history.commit(tx, label);
    if (label !== 'SOLVE') this.solve(merging);
  }

  // -------------------------------------------------------------- constraints

  /** Adds a constraint, re-solves the model, and records both on the undo stack. */
  applyConstraint(constraint: Constraint): void {
    const targets = constraintEntityIds(constraint)
      .map((id) => this.doc.get(id))
      .filter((entity): entity is Entity => Boolean(entity))
      .map((entity) => entity.id);
    if (targets.length === 0) {
      this.message('CONSTRAINT  No geometry to constrain.');
      return;
    }
    const tx = new Transaction();
    tx.addConstraint(constraint);
    this.history.commit(tx, 'CONSTRAINT');
    this.solve(true);
    this.message(`CONSTRAINT  ${describeConstraint(constraint)}`);
  }

  /**
   * Runs the solver over the whole constraint set; returns how many entities moved.
   * `merge` folds the fix-ups into the previous undo step instead of adding one.
   */
  solveNow(merge = false): number {
    if (this.solving) return 0;
    this.solving = true;
    try {
      const { tx, summary } = solveDocument(this.doc, this.settings.enabledConstraints);
      if (tx.isEmpty) return 0;
      if (merge) this.history.extendLast(tx);
      else this.history.commit(tx, 'SOLVE');
      this.reportSolve(summary);
      return tx.ops.length;
    } finally {
      this.solving = false;
    }
  }

  /**
   * Same as `solveNow`, but hands the solve to a worker when the bridge is installed and
   * the model is big enough that the sweep would be felt on the main thread.
   */
  solve(merge = false): void {
    const job = this.offloadSolve;
    if (!job || this.doc.constraintCount < SOLVE_OFFLOAD_THRESHOLD) {
      this.solveNow(merge);
      return;
    }
    if (this.solveInFlight) {
      this.solveQueued = true;
      return;
    }

    const ids = new Set<EntityId>();
    for (const constraint of this.doc.constraints.values()) {
      for (const id of constraintEntityIds(constraint)) ids.add(id);
    }
    const entities: Entity[] = [];
    for (const id of ids) {
      const entity = this.doc.get(id);
      if (entity) entities.push(entity);
    }
    if (entities.length === 0) return;

    this.solveInFlight = true;
    const payload = {
      entities: serializeEntities(entities),
      constraints: stringifyTagged([...this.doc.constraints.values()]),
    };

    void job(payload)
      .then((result) => {
        this.applyOffloadedSolve(result, merge);
      })
      .catch((error: unknown) => {
        this.message(`SOLVE  worker failed (${String(error)}); solving inline.`);
        this.solveNow(merge);
      })
      .finally(() => {
        this.solveInFlight = false;
        if (this.solveQueued) {
          this.solveQueued = false;
          this.solve(false);
        }
      });
  }

  private applyOffloadedSolve(result: { entities: string; changed: number } & SolveSummary, merge: boolean): void {
    const updated = parseEntities(result.entities);
    const tx = new Transaction();
    for (const after of updated) {
      const before = this.doc.get(after.id);
      if (before) tx.update(before, after);
    }
    if (tx.isEmpty) return;
    if (merge) this.history.extendLast(tx);
    else this.history.commit(tx, 'SOLVE');
    this.reportSolve(result);
    this.events.emit('overlay', undefined);
  }

  private reportSolve(summary: SolveSummary): void {
    if (summary.residual > 1e-3) {
      this.message(
        `SOLVE  ${summary.satisfied}/${summary.total} satisfied, residual ${summary.residual.toFixed(4)} mm — geometry is over-constrained.`,
      );
    }
  }

  /** Deletes the constraints of the selected entities (all when nothing is selected). */
  removeConstraints(): number {
    const scope = this.selection.size > 0 ? new Set(this.selection) : null;
    const tx = new Transaction();
    let count = 0;
    for (const constraint of [...this.doc.constraints.values()]) {
      const ids = constraintEntityIds(constraint);
      if (scope && !ids.some((id) => scope.has(id))) continue;
      tx.removeConstraint(constraint);
      count += 1;
    }
    if (count === 0) {
      this.message('DELCONSTRAINTS  Nothing to delete.');
      return 0;
    }
    this.history.commit(tx, 'DELCONSTRAINTS');
    this.message(`DELCONSTRAINTS  Removed ${count} constraint${count === 1 ? '' : 's'}.`);
    return count;
  }

  // -------------------------------------------------------------- numeric input

  /** Recomputes the dynamic input fields for the current cursor position. */
  refreshFields(): void {
    if (!this.activeTool) {
      this.clearFields();
      return;
    }
    const computed = this.activeTool.getFields(this.cursorWorld) ?? [];
    const token = computed.map((field) => `${field.id}:${field.unit}`).join('|');
    if (token !== this.fieldToken) {
      this.fieldToken = token;
      this.fieldOverrides = {};
      this.activeFieldIndex = 0;
    }
    this.fields = computed.map((field) => ({
      ...field,
      value: this.fieldOverrides[field.id] ?? field.value,
    }));
    if (this.activeFieldIndex >= this.fields.length) this.activeFieldIndex = 0;
    this.events.emit('fields', { fields: this.fields, activeIndex: this.activeFieldIndex });
  }

  private clearFields(): void {
    this.fields = [];
    this.fieldOverrides = {};
    this.activeFieldIndex = 0;
    this.fieldToken = '';
    this.events.emit('fields', { fields: [], activeIndex: 0 });
  }

  cycleField(): void {
    if (this.fields.length === 0) return;
    this.activeFieldIndex = (this.activeFieldIndex + 1) % this.fields.length;
    this.events.emit('fields', { fields: this.fields, activeIndex: this.activeFieldIndex });
  }

  /** Feeds a typed number into the active field (e.g. `50`). */
  applyFieldInput(text: string): boolean {
    if (this.fields.length === 0) return false;
    const raw = text.trim();
    if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(raw)) return false;
    const value = Number(raw);
    if (!Number.isFinite(value)) return false;

    const field = this.fields[this.activeFieldIndex];
    if (!field) return false;
    if (field.min !== undefined && value < field.min) {
      this.message(`${field.label} must be at least ${field.min}.`);
      return true;
    }

    this.fieldOverrides[field.id] = value;
    this.refreshFields();

    const complete = this.fields.every((f) => this.fieldOverrides[f.id] !== undefined);
    if (complete) {
      this.commitFields();
    } else {
      this.activeFieldIndex = Math.min(this.activeFieldIndex + 1, this.fields.length - 1);
      this.events.emit('fields', { fields: this.fields, activeIndex: this.activeFieldIndex });
    }
    return true;
  }

  /**
   * Accepts the field values; the tool decides how to advance. Cursor-derived values only count
   * when the caller asks for them — a bare `Enter` must not land a point on its own.
   */
  commitFields(allowCursorValues = false): boolean {
    if (!this.activeTool || this.fields.length === 0) return false;
    if (!allowCursorValues && Object.keys(this.fieldOverrides).length === 0) return false;
    const values: Record<string, number> = {};
    for (const field of this.fields) {
      values[field.id] = this.fieldOverrides[field.id] ?? field.value;
    }
    this.fieldOverrides = {};
    this.activeFieldIndex = 0;
    this.activeTool.applyFields(values, this.cursorWorld);
    this.refreshFields();
    this.events.emit('overlay', undefined);
    return true;
  }

  /**
   * `Enter`: accept what the user typed, otherwise finish the shape for multi-point tools, or
   * take the rubber-band values for a tool whose whole shape is the current step.
   */
  private acceptStep(): void {
    const tool = this.activeTool;
    if (!tool) return;
    if (this.commitFields()) return;
    if (tool.enterFinishes) {
      tool.enter();
      return;
    }
    if (this.commitFields(true)) return;
    tool.enter();
  }

  clearFieldOverrides(): void {
    if (Object.keys(this.fieldOverrides).length === 0) return;
    this.fieldOverrides = {};
    this.activeFieldIndex = 0;
    this.refreshFields();
  }

  // ------------------------------------------------------------------ tools

  startTool(name: string): boolean {
    const key = name.trim().toLowerCase();
    const entry = this.toolEntries.find((t) => t.names.includes(key));
    if (!entry) return false;
    if (this.activeTool) this.activeTool.cancel();
    this.draft = [];
    const tool = entry.make();
    this.activeTool = tool;
    this.lastCommand = tool.name;
    tool.start(this);
    this.refreshFields();
    this.events.emit('overlay', undefined);
    return true;
  }

  cancelTool(): void {
    if (this.activeTool) this.activeTool.cancel();
    else this.endTool();
  }

  // ------------------------------------------------------------- command line

  execute(raw: string): void {
    const input = raw.trim();

    if (input.length === 0) {
      if (this.activeTool) {
        this.acceptStep();
        return;
      }
      if (this.lastCommand) this.execute(this.lastCommand);
      return;
    }

    const tool = this.activeTool;
    if (tool) {
      if (tool.input(input)) {
        this.events.emit('overlay', undefined);
        return;
      }

      // Coordinates stay unambiguous: `x,y`, `@dx,dy` and `dist<angle`.
      const looksLikePoint = input.startsWith('@') || input.includes(',') || input.includes('<');
      if (!looksLikePoint && this.applyFieldInput(input)) return;

      const point = this.parsePoint(input, tool.referencePoint ?? this.reference);
      if (point) {
        this.fieldOverrides = {};
        tool.point(point);
        this.refreshFields();
        this.events.emit('overlay', undefined);
        return;
      }
      if (this.applyFieldInput(input)) return;

      this.message(`Invalid input: ${input}`);
      return;
    }

    if (this.runGlobalCommand(input)) return;
    this.message(`Unknown command: ${input}`);
  }

  /** Accepts `x,y`, `@dx,dy`, `dist<angle`, `@dist<angle`. */
  parsePoint(text: string, reference?: Vec2): Vec2 | null {
    let value = text.trim();
    let relative = false;
    if (value.startsWith('@')) {
      relative = true;
      value = value.slice(1).trim();
    }

    const polar = /^(-?\d*\.?\d+)\s*<\s*(-?\d*\.?\d+)$/.exec(value);
    if (polar) {
      const distance = Number(polar[1]);
      const angle = deg2rad(Number(polar[2]));
      const base = relative ? (reference ?? Vec2.of(0, 0)) : Vec2.of(0, 0);
      return base.add(Vec2.polar(distance, angle));
    }

    const cartesian = /^(-?\d*\.?\d+)\s*,\s*(-?\d*\.?\d+)$/.exec(value);
    if (cartesian) {
      const x = Number(cartesian[1]);
      const y = Number(cartesian[2]);
      return relative ? (reference ?? Vec2.of(0, 0)).add(Vec2.of(x, y)) : Vec2.of(x, y);
    }

    return null;
  }

  private runGlobalCommand(input: string): boolean {
    const parts = input.split(/\s+/);
    const head = (parts[0] ?? '').toLowerCase();
    const args = parts.slice(1);

    switch (head) {
      case 'undo':
      case 'u':
        this.undo();
        return true;
      case 'redo':
        this.redo();
        return true;
      case 'zoom':
      case 'z':
        this.zoomExtents();
        this.message('ZOOM  Extents');
        return true;
      case 'layer':
      case 'la':
        this.handleLayerCommand(args);
        return true;
      case 'grid':
        this.setGridVisible(!this.settings.grid.visible);
        this.message(`Grid ${this.settings.grid.visible ? 'on' : 'off'}`);
        return true;
      case 'precision':
      case 'prec': {
        const decimals = Number(args[0]);
        if (Number.isFinite(decimals) && decimals >= 0 && decimals <= 6) {
          this.settings.lengthDecimals = Math.round(decimals);
          this.message(`Precision: ${this.settings.lengthDecimals} decimals (mm)`);
        } else {
          this.message(`PRECISION 0-6  (current: ${this.settings.lengthDecimals})`);
        }
        return true;
      }
      case 'dimvalue':
      case 'dv':
      case 'dimset': {
        // One number, one selected dimension: type the measurement the drawing should have.
        const value = Number(args[0]);
        const selected = this.selectedEntities().filter((entity) => entity.type === 'dimension');
        if (selected.length !== 1) {
          this.message('DIMVALUE  <mm> — select exactly one dimension first.');
          return true;
        }
        if (!Number.isFinite(value)) {
          this.message('DIMVALUE  <mm> — give the new measurement.');
          return true;
        }
        this.setDimensionValue(selected[0]!.id, value);
        return true;
      }
      case 'help':
      case '?':
        this.message(
          `Commands: ${this.toolEntries.map((t) => t.names[0]!.toUpperCase()).join(', ')}, UNDO, REDO, ZOOM, LAYER, CONSTRAINTS, SOLVE, DELCONSTRAINTS, BROWSER`,
        );
        return true;
      case 'browser':
      case 'caps': {
        void probeCapabilities().then((caps) => {
          // A second requestAdapter() can come back null even while WebGPU is rendering, so the
          // renderer that is actually drawing wins for the WebGPU row.
          const backend = document.getElementById('status-backend')?.textContent?.toLowerCase();
          const rendering = backend === 'webgpu' || backend === 'webgl';
          const report = formatCapabilities({
            ...caps,
            webgpuAdapter: caps.webgpuAdapter || backend === 'webgpu',
          });
          console.log(report);
          this.message(
            `BROWSER  ${caps.browser.name} · rendering ${rendering ? backend!.toUpperCase() : 'not started'} · ` +
              `browser storage ${caps.opfs ? 'yes' : 'no'} · folder linking ${caps.folderLink ? 'yes' : 'no'} ` +
              '(Edge/Chrome only) — full report in the console',
          );
        });
        return true;
      }
      case 'constraints':
      case 'clist':
        this.message(
          `CONSTRAINTS  ${this.doc.constraintCount} active — ${describeConstraintSet(this.doc)}`,
        );
        return true;
      case 'solve':
        this.solve(false);
        this.message('SOLVE  running…');
        return true;
      case 'delconstraints':
      case 'dc':
        this.removeConstraints();
        return true;
      default:
        break;
    }

    return this.startTool(head);
  }

  private handleLayerCommand(args: string[]): void {
    const sub = (args[0] ?? '').toLowerCase();
    const name = args[1];
    const doc = this.doc;

    if (sub === '' || sub === 'list') {
      const names = [...doc.layers.values()].map(
        (layer) => `${layer.name}${layer.id === doc.currentLayerId ? '*' : ''}`,
      );
      this.message(`Layers: ${names.join(', ')}`);
      return;
    }
    if (sub === 'set' && name) {
      doc.setCurrentLayer(name);
      this.message(`Current layer: ${name}`);
      return;
    }
    if (sub === 'new' && name) {
      doc.ensureLayer(name);
      doc.setCurrentLayer(name);
      this.message(`Layer created: ${name}`);
      return;
    }
    if (sub === 'off' && name) {
      doc.updateLayer(name, { visible: false });
      return;
    }
    if (sub === 'on' && name) {
      doc.updateLayer(name, { visible: true });
      return;
    }
    this.message('LAYER [LIST|SET|NEW|ON|OFF] <name>');
  }

  // ------------------------------------------------------------------ history

  undo(): boolean {
    const label = this.history.undo();
    if (!label) {
      this.message('Nothing to undo.');
      return false;
    }
    this.dropMissingSelection();
    this.message(`Undo: ${label}`);
    this.events.emit('overlay', undefined);
    return true;
  }

  redo(): boolean {
    const label = this.history.redo();
    if (!label) {
      this.message('Nothing to redo.');
      return false;
    }
    this.dropMissingSelection();
    this.message(`Redo: ${label}`);
    this.events.emit('overlay', undefined);
    return true;
  }

  private dropMissingSelection(): void {
    for (const id of [...this.selection]) {
      if (!this.doc.has(id)) this.selection.delete(id);
    }
    this.events.emit('selection', this.selection.size);
  }

  // ----------------------------------------------------------------- selection

  setSelection(ids: Iterable<EntityId>): void {
    const list = [...ids];
    this.selection.clear();
    for (const id of list) {
      if (this.doc.has(id)) this.selection.add(id);
    }
    this.events.emit('selection', this.selection.size);
    this.events.emit('overlay', undefined);
  }

  toggleSelection(id: EntityId): void {
    if (this.selection.has(id)) this.selection.delete(id);
    else if (this.doc.has(id)) this.selection.add(id);
    this.events.emit('selection', this.selection.size);
    this.events.emit('overlay', undefined);
  }

  clearSelection(): void {
    this.setSelection([]);
  }

  selectAll(): void {
    this.setSelection(this.doc.entities.keys());
  }

  selectedEntities(): Entity[] {
    const out: Entity[] = [];
    for (const id of this.selection) {
      const entity = this.doc.get(id);
      if (entity) out.push(entity);
    }
    return out;
  }

  deleteSelection(): boolean {
    if (this.selection.size === 0) return false;
    const tx = new Transaction();
    const constraints = new Map<string, Constraint>();
    for (const id of this.selection) {
      for (const constraint of this.doc.constraintsOf(id)) constraints.set(constraint.id, constraint);
    }
    for (const constraint of constraints.values()) tx.removeConstraint(constraint);
    for (const entity of this.selectedEntities()) tx.remove(entity);
    this.history.commit(tx, 'ERASE');
    this.selection.clear();
    this.events.emit('selection', 0);
    this.events.emit('overlay', undefined);
    return true;
  }

  pickAt(world: Vec2, tolerancePx = this.settings.pickRadiusPx): EntityId | null {
    return hitTest(this.doc, world, this.viewport.pxToWorld(tolerancePx));
  }

  // --------------------------------------------------------------- dimensions

  /**
   * A dimension that only reports a number is half a tool: typing a new measurement moves its
   * second origin and drags every handle sitting on it, so the geometry follows. `p1` stays put,
   * and the change is one undoable step.
   */
  setDimensionValue(id: EntityId, value: number): boolean {
    const entity = this.doc.get(id);
    if (!entity || entity.type !== 'dimension') return false;
    const axes = dimensionAxes(entity);
    if (!axes) {
      this.message('DIM  This dimension has no measurable direction.');
      return false;
    }
    if (!(value > 0)) {
      this.message('DIM  The measurement must be greater than zero.');
      return false;
    }
    const sign = entity.p2.sub(entity.p1).dot(axes.dir) < 0 ? -1 : 1;
    const target = entity.p1.add(axes.dir.scale(sign * value));
    const delta = target.sub(entity.p2);
    if (delta.len() < 1e-9) return true;

    const tx = new Transaction();
    tx.update(entity, { ...entity, p2: target });
    for (const other of this.doc.entities.values()) {
      if (other.id === entity.id) continue;
      const handle = entityHandles(other).find((point) => point.point.distanceTo(entity.p2) <= 1e-6);
      if (!handle) continue;
      tx.update(other, withHandlePoint(other, handle.name, handle.point.add(delta)));
    }
    this.commit(tx, 'DIM');
    this.message(`DIM  ${value.toFixed(this.settings.lengthDecimals)} mm`);
    return true;
  }

  /** The dimension under the cursor, for the inline editor (double-click). */
  dimensionAt(screen: Vec2): EntityId | null {
    const hit = this.pickAt(this.viewport.screenToWorld(screen));
    const entity = hit ? this.doc.get(hit) : undefined;
    return entity?.type === 'dimension' ? entity.id : null;
  }

  /** Opens the inline measurement editor for the dimension under the cursor. */
  editDimensionAt(screen: Vec2): boolean {
    const id = this.dimensionAt(screen);
    if (!id) return false;
    this.events.emit('dimensionEdit', { id });
    return true;
  }

  /** The measurement and label position of a dimension, for the inline editor. */
  dimensionReadout(id: EntityId): { value: number; decimals: number; position: Vec2 } | null {
    const entity = this.doc.get(id);
    if (!entity || entity.type !== 'dimension') return null;
    return {
      value: dimensionValue(entity),
      decimals: Math.max(0, Math.min(entity.precision, 6)),
      position: dimensionGeometry(entity).text.position,
    };
  }

  // ------------------------------------------------------------------ pointer

  private computeSnap(screen: Vec2): { raw: Vec2; candidates: SnapCandidate[] } {
    const raw = this.viewport.screenToWorld(screen);
    const reference = this.snappingReference();
    const radius = this.viewport.pxToWorld(this.settings.snapRadiusPx);
    const candidates = this.snapEngine.compute(this.doc, raw, radius, reference);
    return { raw, candidates };
  }

  private snappingReference(): Vec2 | undefined {
    return this.activeTool?.referencePoint ?? this.reference;
  }

  private constrainPoint(point: Vec2): Vec2 {
    if (!this.angleConstraint) return point;
    if (this.activeTool && !this.activeTool.supportsAngleConstraint) return point;
    const reference = this.snappingReference();
    if (!reference) return point;
    return constrainToAngleStep(reference, point, this.settings.polarStepDeg);
  }

  /** Ctrl held / released: constrain the cursor to 45° rays (or the configured step). */
  setAngleConstraint(enabled: boolean): void {
    if (this.angleConstraint === enabled) return;
    this.angleConstraint = enabled;
    if (this.activeTool) {
      this.updateCursor(this.cursorScreen);
      this.activeTool.hover(this.cursorWorld);
      this.refreshFields();
    }
    this.events.emit('overlay', undefined);
  }

  updateCursor(screen: Vec2): Vec2 {
    this.cursorScreen = screen;
    const { raw, candidates } = this.computeSnap(screen);
    this.snapCandidates = candidates;
    this.snapCycle = -1;
    const chosen = candidates[0] ?? null;
    this.activeSnap = chosen;
    this.cursorWorld = this.constrainPoint(chosen ? chosen.point : raw);
    this.events.emit('cursor', { world: this.cursorWorld, screen, snap: chosen });
    return this.cursorWorld;
  }

  pointerMove(screen: Vec2): void {
    const world = this.updateCursor(screen);
    if (this.selectionStart) this.selectionEnd = world;
    if (this.activeTool) this.activeTool.hover(world);
    this.refreshFields();
    this.events.emit('overlay', undefined);
  }

  pointerDown(screen: Vec2, button: MouseButton, modifiers: Modifiers): void {
    this.setAngleConstraint(modifiers.ctrl);
    if (button === 'right') {
      if (this.activeTool) this.activeTool.cancel();
      else this.clearSelection();
      return;
    }
    if (button !== 'left') return;

    const world = this.updateCursor(screen);

    if (this.activeTool) {
      this.activeTool.point(world);
      this.events.emit('overlay', undefined);
      return;
    }

    const hit = this.pickAt(world);
    if (hit) {
      if (modifiers.shift) this.toggleSelection(hit);
      else this.setSelection([hit]);
      return;
    }

    if (!modifiers.shift) this.setSelection([]);
    this.selectionStart = world;
    this.selectionEnd = world;
    this.events.emit('overlay', undefined);
  }

  pointerUp(screen: Vec2, modifiers: Modifiers): void {
    if (!this.selectionStart) return;
    const start = this.selectionStart;
    const end = this.selectionEnd ?? this.viewport.screenToWorld(screen);
    this.selectionStart = undefined;
    this.selectionEnd = undefined;

    const dragPx = start.distanceTo(end) * this.viewport.scale;
    if (dragPx < 4) {
      this.clearSelection();
      return;
    }

    const box = BBox.fromCorners(start, end);
    const ids = end.x >= start.x ? entitiesInWindow(this.doc, box) : entitiesCrossing(this.doc, box);
    this.setSelection(modifiers.shift ? [...this.selection, ...ids] : ids);
  }

  cycleSnap(): void {
    if (this.snapCandidates.length === 0) return;
    this.snapCycle = (this.snapCycle + 1) % this.snapCandidates.length;
    const chosen = this.snapCandidates[this.snapCycle] ?? null;
    this.activeSnap = chosen;
    if (chosen) this.cursorWorld = this.constrainPoint(chosen.point);
    this.events.emit('cursor', {
      world: this.cursorWorld,
      screen: this.cursorScreen,
      snap: chosen,
    });
    if (this.activeTool) this.activeTool.hover(this.cursorWorld);
    this.events.emit('overlay', undefined);
  }

  // ----------------------------------------------------------------- keyboard

  keyDown(event: { key: string; ctrlKey: boolean; shiftKey: boolean }): boolean {
    if (event.key === 'Escape') {
      this.fieldOverrides = {};
      this.activeFieldIndex = 0;
      if (this.activeTool) this.activeTool.cancel();
      else this.clearSelection();
      this.selectionStart = undefined;
      this.selectionEnd = undefined;
      this.events.emit('overlay', undefined);
      return true;
    }
    if (event.key === 'Enter') {
      if (this.activeTool) {
        this.acceptStep();
        return true;
      }
      if (this.lastCommand) {
        this.execute(this.lastCommand);
        return true;
      }
      return false;
    }
    if (event.key === 'Delete') {
      return this.deleteSelection();
    }
    if (event.ctrlKey && event.key.toLowerCase() === 'z') {
      if (event.shiftKey) this.redo();
      else this.undo();
      return true;
    }
    if (event.ctrlKey && event.key.toLowerCase() === 'y') {
      this.redo();
      return true;
    }
    if (event.ctrlKey && event.key.toLowerCase() === 'a') {
      this.selectAll();
      return true;
    }
    if (event.key === 'Tab') {
      this.cycleSnap();
      return true;
    }
    return false;
  }

  // -------------------------------------------------------------------- view

  resize(width: number, height: number): void {
    this.viewport.setSize(width, height);
    this.events.emit('view', undefined);
    this.events.emit('overlay', undefined);
  }

  zoomAt(screen: Vec2, factor: number): void {
    this.viewport.zoomAtScreen(screen, factor);
    this.events.emit('view', undefined);
    this.events.emit('overlay', undefined);
  }

  panBy(dx: number, dy: number): void {
    this.viewport.panByScreen(dx, dy);
    this.events.emit('view', undefined);
    this.events.emit('overlay', undefined);
  }

  zoomExtents(): void {
    const bounds = this.doc.bounds(true);
    this.viewport.zoomToRect(bounds);
    this.doc.reindex();
    this.events.emit('view', undefined);
    this.events.emit('overlay', undefined);
  }

  setGridVisible(visible: boolean): void {
    this.settings.grid.visible = visible;
    this.events.emit('view', undefined);
  }

  setGridSize(size: number): void {
    this.settings.grid.size = Math.max(size, 1e-3);
    this.snapEngine.settings.gridSize = this.settings.grid.size;
  }
}
